import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Archive, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { products } from '../api';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import type { Product, ProductStatus } from '../types';

interface ProductForm {
  name: string;
  slug: string;
  description: string;
  status: ProductStatus;
}

const DEFAULT_FORM: ProductForm = {
  name: '',
  slug: '',
  description: '',
  status: 'DRAFT',
};

export default function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<ProductForm>(DEFAULT_FORM);
  const [formError, setFormError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: products.list,
  });

  const createMutation = useMutation({
    mutationFn: (payload: ProductForm) => products.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setShowModal(false);
      setForm(DEFAULT_FORM);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to create product.';
      setFormError(msg);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => products.update(id, { status: 'ARCHIVED' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => products.purge(id),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['products'] }); if (r?.message) alert(r.message); },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.'),
  });

  function handleSlugify(name: string) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  const columns: Column<Product>[] = [
    { key: 'name', label: 'Name' },
    { key: 'slug', label: 'Slug', render: (r) => <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{r.slug}</code> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} type="product" /> },
    {
      key: 'plans',
      label: 'Plans',
      render: (r) => (
        <span className="text-slate-500 text-xs">{r._count?.plans ?? '—'}</span>
      ),
    },
    { key: 'woocommerceProductId', label: 'WC Product ID', render: (r) => <span className="text-slate-500 text-xs">{r.woocommerceProductId ?? '—'}</span> },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/products/${r.id}`)}
            className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
          >
            <Eye size={12} /> View
          </button>
          {r.status !== 'ARCHIVED' && (
            <button
              onClick={() => {
                if (confirm(t('products.archiveConfirm', { name: r.name })))
                  archiveMutation.mutate(r.id);
              }}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              <Archive size={12} /> {t('products.archive')}
            </button>
          )}

          {/* Restlos löschen — gedacht für den Vertipper beim Anlegen.
              Nur angeboten, solange keine Instanz daran hängt; das prüft die
              Route noch einmal selbst, denn zwischen Anzeigen und Klicken kann
              eine entstehen. */}
          {(r._count?.instances ?? 0) === 0 && (
            <button
              onClick={() => {
                if (confirm(t('products.deleteConfirm', { name: r.name })))
                  deleteMutation.mutate(r.id);
              }}
              className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"
            >
              <Trash2 size={12} /> {t('common.delete')}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{data?.length ?? 0} products</p>
        <button
          onClick={() => { setShowModal(true); setFormError(''); }}
          className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors"
        >
          <Plus size={15} /> {t('products.newProduct')}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data ?? []}
        loading={isLoading}
        emptyMessage="No products yet."
        rowKey={(r) => r.id}
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900">{t('products.newProduct')}</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFormError('');
                createMutation.mutate(form);
              }}
              className="px-6 py-5 space-y-4"
            >
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      name: e.target.value,
                      slug: handleSlugify(e.target.value),
                    }))
                  }
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Slug</label>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ProductStatus }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="DEPRECATED">Deprecated</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
                >
                  {createMutation.isPending ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
